import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LayoutDashboard, FileText, CalendarClock, UserCircle, Settings, LogOut, HelpCircle, Clock, LifeBuoy, Route as RouteIcon } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { Logo } from "@/components/Logo";
import { NotificationBell } from "@/components/NotificationBell";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { TourGuide } from "@/components/TourGuide";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

export default function WorkerLayout() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tourOpen, setTourOpen] = useState(false);
  const [timeTracking, setTimeTracking] = useState(false);
  const [care, setCare] = useState(false);
  useEffect(() => { api.get("/worker/config").then((r) => { setTimeTracking(!!r.data.time_tracking); setCare(!!r.data.care); }).catch(() => {}); }, []);
  const tourSteps = [
    { title: t("tour.w0t"), body: t("tour.w0b") },
    { selector: '[data-testid="wnav-documents"], [data-testid="wtab-documents"]', title: t("tour.w1t"), body: t("tour.w1b") },
    { selector: '[data-testid="wnav-deadlines"], [data-testid="wtab-deadlines"]', title: t("tour.w2t"), body: t("tour.w2b") },
    { selector: '[data-testid="wnav-profile"], [data-testid="wtab-profile"]', title: t("tour.w3t"), body: t("tour.w3b") },
  ];
  const endTour = () => { localStorage.setItem("eg_worker_tour_done", "1"); setTourOpen(false); };
  useEffect(() => {
    if (!localStorage.getItem("eg_worker_tour_done")) {
      const id = setTimeout(() => setTourOpen(true), 1200);
      return () => clearTimeout(id);
    }
  }, []);
  const links = [
    { to: "/app/worker", label: t("wNav.overview"), icon: LayoutDashboard, end: true, key: "overview" },
    { to: "/app/worker/documents", label: t("wNav.documents"), icon: FileText, key: "documents" },
    { to: "/app/worker/deadlines", label: t("wNav.deadlines"), icon: CalendarClock, key: "deadlines" },
    ...(timeTracking ? [{ to: "/app/worker/time", label: t("wNav.myTime"), icon: Clock, key: "time" }] : []),
    ...(care ? [{ to: "/app/worker/care", label: "Meine Tour", icon: RouteIcon, key: "care" }] : []),
    { to: "/app/worker/profile", label: t("wNav.profile"), icon: UserCircle, key: "profile" },
    { to: "/app/worker/support", label: t("sup.nav"), icon: LifeBuoy, key: "support" },
    { to: "/app/worker/settings", label: t("wNav.settings"), icon: Settings, key: "settings" },
  ];

  return (
    <div className="min-h-screen bg-background pb-20 lg:pb-0">
      <header className="glass-nav sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-slate-200 px-4 sm:px-6">
        <Link to="/app/worker"><Logo /></Link>
        <nav className="ml-6 hidden items-center gap-1 lg:flex">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end} data-testid={`wnav-${l.key}`}
              className={({ isActive }) => `flex items-center gap-2 rounded-full px-3 py-2 text-sm font-medium transition-colors ${isActive ? "bg-cyan/10 text-cyan-dark" : "text-slate-600 hover:bg-slate-100"}`}>
              <l.icon className="h-4 w-4" /> {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="ml-auto flex items-center gap-2">
          <button onClick={() => setTourOpen(true)} data-testid="worker-tour-start-btn" title={t("tour.wHelp")} aria-label={t("tour.wHelp")} className="flex h-9 w-9 items-center justify-center rounded-full text-slate-500 transition-colors hover:bg-slate-100 hover:text-navy"><HelpCircle className="h-5 w-5" /></button>
          <LanguageSwitcher />
          <NotificationBell />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex h-8 w-8 items-center justify-center rounded-full bg-navy text-xs font-bold text-white" data-testid="worker-profile-menu">
                {(user?.first_name?.[0] || "") + (user?.last_name?.[0] || "")}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuLabel>{user?.first_name} {user?.last_name}<div className="text-xs font-normal text-slate-400">{user?.email}</div></DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => nav("/app/worker/profile")}>{t("wNav.myProfile")}</DropdownMenuItem>
              <DropdownMenuItem onClick={async () => { await logout(); nav("/login"); }} data-testid="worker-logout-btn" className="text-red-600"><LogOut className="mr-2 h-4 w-4" /> {t("wNav.logout")}</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-6 sm:px-6"><Outlet /></main>

      <nav className="glass-nav fixed inset-x-0 bottom-0 z-30 flex items-center justify-around border-t border-slate-200 px-2 py-2 lg:hidden">
        {links.map((l) => (
          <NavLink key={l.to} to={l.to} end={l.end} data-testid={`wtab-${l.key}`}
            className={({ isActive }) => `flex flex-1 flex-col items-center gap-1 rounded-lg py-1 text-[10px] font-medium transition-colors ${isActive ? "text-cyan-dark" : "text-slate-400"}`}>
            <l.icon className="h-5 w-5" /> {l.label}
          </NavLink>
        ))}
      </nav>
      <TourGuide steps={tourSteps} open={tourOpen} onClose={endTour} />
    </div>
  );
}
